import $http from '@/services/common/http.service';
import {Coach, CoachClient, Paging, User} from '@/store/models';
import jwtService from '@/services/common/jwt.service';
import $backgroundService from '@/services/common/background.service';

export class CoachService {
  public async loadCoaches(params?: { search_key?: string; order_by?: string }) {
    const user = await jwtService.getUser();
    const response = await $backgroundService.query(
      `/admins/${user.id}/coaches`,
      params
    );
    return response.data.data as { users: User[]; paging: Paging };
  }

  public async updateCoachDepartment(
    data: { id: number; department: string }[]
  ) {
    const user = await jwtService.getUser();
    const response = await $http.post(
      `/admins/${user.id}/system/bulk-update-manager`,
      data
    );
    return response.data.data as { users: User[]; paging: Paging };
  }

  public async loadCoachByCompany(params?: {
    department?: string;
    companyName?: string;
  }) {
    const user = await jwtService.getUser();
    const response = await $backgroundService.query(`/admins/${user.id}/system/coaches`, params);
    return response.data.data as Coach[];
  }

  public async loadCoachClients(params?: {
    coachId?: string;
  }) {
    const user = await jwtService.getUser();
    const response = await $backgroundService.query(`/admins/${user.id}/system/coach_clients`, params);
    return response.data.data as CoachClient[];
  }

  // Paged variant: passing limit/offset switches the endpoint to paging mode
  // and returns { clients, paging } instead of the flat CoachClient[] above.
  public async loadCoachClientsPaged(params?: {
    search_key?: string;
    order_by?: string;
    offset?: number;
    limit?: number;
    coachId?: string;
  }) {
    const user = await jwtService.getUser();
    const response = await $backgroundService.query(
      `/admins/${user.id}/system/coach_clients`,
      params
    );
    return response.data.data as { clients: CoachClient[]; paging: Paging };
  }

  public async createCoach(coach: {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle?: string;
    companyName?: string;
    department?: string;
    subStartDate?: string;
    subEndDate?: string;
    initialPassword?: string;
    country?: string;
  }) {
    const user = await jwtService.getUser();
    const response = await $http.post(`/admins/${user.id}/coaches`, coach);
    return response.data.data;
  }

  public async updateCoach(
    id: number,
    data: {
      firstName?: string;
      lastName?: string;
      jobTitle?: string;
      companyName?: string;
      subStartDate?: string;
      subEndDate?: string;
      department?: string;
      accountStatus?: number;
      password?: string;
      country?: string;
    }
  ) {
    const user = await jwtService.getUser();
    const response = await $http.update(
      `/admins/${user.id}/coaches`,
      `${id}`,
      data
    );
    return response.data.data as { users: User[]; paging: Paging };
  }

  public async deleteCoach(coachId: number) {
    const user = await jwtService.getUser();
    const response = await $http.delete(`admins/${user.id}/coaches/${coachId}`);
    return response.data.data;
  }

  public async resetPassword(coachId: number, data: { password: string }) {
    const user = await jwtService.getUser();
    const response = await $http.put(
      `admins/${user.id}/coaches/${coachId}/reset-password`,
      data
    );
    return response.data.data;
  }

  public async resetCoachClientPassword(
    coachClientId: number,
    data: { password: string }
  ) {
    const user = await jwtService.getUser();
    const response = await $http.put(
      `admins/${user.id}/system/coach_clients/${coachClientId}/reset-password`,
      data
    );
    return response.data.data;
  }

  public async import(file: File) {
    const formData = new FormData();
    const user = await jwtService.getUser();
    formData.append('file', file, file.name);
    const response = await $http.postWithFormData(
      `admins/${user.id}/coaches/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  }
}

const coachService = new CoachService();
export default coachService;
